import React, { useState, useEffect, useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import { useAuth } from '../context/AuthContext';
import { getCountries, getQuote, getPublicContent, getLogoConfig, getPlans, getPublicBlogPosts, getPublicStaticPages } from '../lib/api';
import { defaultLogoState, resolveImageUrl, withResolvedLogo } from '../lib/branding';
import { ResilientImg } from '../components/ui/ResilientImg';
import { useNavigate, Link } from 'react-router-dom';
import { Package, Globe, Truck, Shield, ChevronLeft, ChevronRight, ArrowRight, Calculator, MapPin, Box, CreditCard, Star, ChevronDown, Mail, Phone, UserPlus, ShoppingBag, ShoppingCart, Warehouse, Combine, Ruler } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { MarketingHeader } from '../components/marketing/MarketingLayout';

/* ─── Hardcoded defaults (used when no content_config saved yet) ─── */
const DEFAULTS = {
  hero: {
    badge: 'TRUSTED BY 10,000+ NRIs WORLDWIDE',
    title_line1: 'Ship from India.',
    title_line2_prefix: 'Deliver to the ',
    title_line2_accent: 'World.',
    subtitle: 'Ship from India to the USA, Canada, and the UK with a free virtual Mumbai address. Shop Indian stores, consolidate packages, and forward them worldwide with ShipDesi.',
    cta_primary: 'Get Your Free Address',
    cta_secondary: 'Calculate Shipping',
    autoplay_seconds: 3,
    cards: [
      {
        id: 'nri',
        label: 'FOR NRIs',
        label_color: '#D96245',
        icon: 'shopping-bag',
        title_line1: 'Shop in India,',
        title_line2: 'Ship to Your Doorstep',
        desc: 'Get a free Indian address, shop anywhere in India, consolidate and ship worldwide at the best rates.',
        image: 'https://files.shipdesi.agrenai.online/shipdesi/public/hero_card_nri_94a8e90f.png',
        bg: '#FBE7DA',
        cta: 'Learn More',
        cta_href: '/package-forwarding',
      },
      {
        id: 'shopper',
        label: 'PERSONAL SHOPPER',
        label_color: '#3A805A',
        icon: 'shopping-cart',
        title_line1: 'We Buy It for You',
        title_line2: 'in India',
        desc: "Can't find it or sites don't accept your card? Our team buys, inspects and ships it to you.",
        image: 'https://files.shipdesi.agrenai.online/shipdesi/public/hero_card_shopper_6691e18e.png',
        bg: '#E1F1E5',
        cta: 'Learn More',
        cta_href: '/personal-shopper',
      },
      {
        id: 'business',
        label: 'FOR BUSINESSES',
        label_color: '#6366F1',
        icon: 'warehouse',
        title_line1: 'Fulfill Global Orders',
        title_line2: 'from India',
        desc: 'Store your inventory with us and we pick, pack and ship directly to your customers worldwide.',
        image: 'https://files.shipdesi.agrenai.online/shipdesi/public/hero_card_business_49616de5.png',
        bg: '#ECE6F5',
        // Sampled from the image's own top row. It sits 63 units away from `bg`,
        // far enough that the fade alone still showed a band. The other two
        // cards are within 12 and 48 and need no override.
        image_bg: '#D6CCE6',
        cta: 'Learn More',
        cta_href: '/fulfillment',
      },
    ],
  },
  calculator: { label: 'SHIPPING CALCULATOR', title: 'Estimate Your Shipping Cost' },
  how_it_works: {
    label: 'HOW IT WORKS', title: 'Three Simple Steps',
    steps: [
      { title: 'Get Your Address', desc: 'Sign up and receive your unique Indian warehouse address instantly.' },
      { title: 'We Receive & Store', desc: 'Shop from any Indian store. We receive and safely store your items.' },
      { title: 'Ship Worldwide', desc: 'Consolidate and ship. Choose from DHL, FedEx, or UPS.' },
    ],
  },
  services: {
    label: 'OUR SERVICES', title: 'Everything You Need',
    items: [
      { title: 'Package Consolidation', desc: 'Combine multiple packages into one shipment and save up to 80% on shipping.' },
      { title: 'Global Shipping', desc: 'Ship to 15+ countries via DHL, FedEx, and UPS at competitive rates.' },
      { title: 'Package Insurance', desc: 'Protect your shipments with comprehensive insurance coverage.' },
      { title: 'Personal Shopping', desc: "Can't buy from an Indian store? We'll shop for you." },
    ],
  },
  pricing: {
    label: 'PRICING', title: 'Simple, Transparent Pricing',
    plans: [
      // Fallback only: the section shows the live plans from /api/plans.
      { name: 'Starter', price: 'Free', features: ['Virtual Indian Address', 'Consolidation at ₹50/package', '20 days free storage', 'Chat support'], cta: 'Get Started' },
      { name: 'Pro', price: '₹499/mo', features: ['Everything in Starter', 'Free consolidation', '3% shipping discount', 'Personal Shopper at 3%', '30 days free storage', 'Chat + Email support'], cta: 'Go Pro', popular: true },
      { name: 'Business', price: '₹3,999/yr', features: ['Everything in Pro', '5% shipping discount', 'Free Personal Shopper', '50 days free storage', 'Chat + Email + Phone support'], cta: 'Get Business' },
    ],
  },
  faq: {
    label: 'FAQ', title: 'Common Questions',
    items: [
      { q: 'How does the virtual address work?', a: 'Once you sign up, you get a unique Indian warehouse address. Shop from any Indian store and ship to this address. We receive, store, and forward your packages internationally.' },
      { q: 'How much can I save with consolidation?', a: 'By combining multiple packages into one shipment, you can save 60-80% on international shipping costs compared to shipping items individually.' },
      { q: 'Which countries do you ship to?', a: 'We ship to 15+ countries including USA, UK, Canada, Australia, Singapore, UAE, Germany, Japan and more.' },
      { q: 'How long does shipping take?', a: 'Depending on the destination and courier selected, delivery typically takes 3-9 business days.' },
      { q: 'Is my package insured?', a: 'Basic insurance is included. Premium insurance options are available for high-value items.' },
    ],
  },
  footer: {
    tagline: "India's premier parcel forwarding service. Connecting the Indian diaspora with home, one package at a time.",
    email: 'support@shipindia.com',
    phone: '+91 22 1234 5678',
    address: 'Mumbai, Maharashtra, India',
  },
};

function mergeDeep(target, source) {
  const result = { ...target };
  for (const key of Object.keys(source)) {
    if (source[key] && typeof source[key] === 'object' && !Array.isArray(source[key])) {
      result[key] = mergeDeep(target[key] || {}, source[key]);
    } else if (source[key] !== undefined) {
      result[key] = source[key];
    }
  }
  return result;
}

/* ─── Hero service cards (3-up on lg, 1-at-a-time carousel below lg) ─── */
const CARD_ICONS = {
  'shopping-bag': ShoppingBag,
  'shopping-cart': ShoppingCart,
  warehouse: Warehouse,
};

function HeroCards({ cards = [], navigate, user, autoplaySeconds = 0 }) {
  const [active, setActive] = useState(0);
  const [paused, setPaused] = useState(false);
  const resumeTimerRef = useRef(null);
  const total = cards.length || 0;

  // Auto-rotate every `autoplaySeconds`. `0` disables. Only runs on mobile/
  // tablet — on desktop all 3 cards are rendered side-by-side so the active
  // index has no visual effect. We key re-runs on paused + autoplaySeconds
  // only so the interval isn't recreated on every re-render.
  useEffect(() => {
    if (!total || !autoplaySeconds || autoplaySeconds <= 0 || paused) return undefined;
    const id = setInterval(
      () => setActive(i => (i + 1) % total),
      Math.max(1, autoplaySeconds) * 1000,
    );
    return () => clearInterval(id);
  }, [total, autoplaySeconds, paused]);

  // Pause briefly after a manual carousel interaction, then auto-resume.
  // Important: we deliberately do NOT pause on onTouchStart — mobile users
  // scroll through the page and that would permanently freeze autoplay
  // (onTouchEnd doesn't always fire on scroll gestures). We only pause on
  // hover (desktop) and on explicit button/dot clicks.
  const pauseThenResume = (ms = 6000) => {
    if (resumeTimerRef.current) clearTimeout(resumeTimerRef.current);
    setPaused(true);
    resumeTimerRef.current = setTimeout(() => setPaused(false), ms);
  };
  useEffect(() => () => { if (resumeTimerRef.current) clearTimeout(resumeTimerRef.current); }, []);

  if (!total) return null;
  const goPrev = () => { pauseThenResume(); setActive(i => (i - 1 + total) % total); };
  const goNext = () => { pauseThenResume(); setActive(i => (i + 1) % total); };

  return (
    <div
      className="relative animate-fade-in-up delay-200"
      data-testid="hero-cards"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      {/* Mobile / tablet — sliding strip. All 3 cards mount simultaneously so
          every image downloads on first paint and swaps are instantaneous
          (no image "lag" when autoplay advances the active index). */}
      <div className="lg:hidden relative px-10">
        <button onClick={goPrev} aria-label="Previous"
          data-testid="hero-cards-prev"
          className="absolute left-0 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-white border border-[#EBE8E4] flex items-center justify-center hover:bg-[#F6F5F2] transition-colors z-10">
          <ChevronLeft className="w-5 h-5 text-[#1A1B1E]" />
        </button>
        <button onClick={goNext} aria-label="Next"
          data-testid="hero-cards-next"
          className="absolute right-0 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-white border border-[#EBE8E4] flex items-center justify-center hover:bg-[#F6F5F2] transition-colors z-10">
          <ChevronRight className="w-5 h-5 text-[#1A1B1E]" />
        </button>
        <div className="overflow-hidden">
          <div
            className="flex"
            style={{
              transform: `translate3d(-${active * 100}%, 0, 0)`,
              transition: 'transform 500ms cubic-bezier(0.4, 0, 0.2, 1)',
              willChange: 'transform',
            }}
          >
            {cards.map(card => (
              <div key={card.id || card.title_line1} className="min-w-full">
                <HeroCard card={card} navigate={navigate} user={user} />
              </div>
            ))}
          </div>
        </div>
        <div className="flex justify-center gap-2 mt-5" data-testid="hero-cards-dots">
          {cards.map((card, i) => (
            <button key={card.id || i}
              onClick={() => { pauseThenResume(); setActive(i); }}
              aria-label={`Go to card ${i + 1}`}
              data-testid={`hero-cards-dot-${i}`}
              className={`h-2 rounded-full transition-all ${i === active ? 'w-6 bg-[#1A1B1E]' : 'w-2 bg-[#1A1B1E]/25 hover:bg-[#1A1B1E]/45'}`} />
          ))}
        </div>
      </div>

      {/* Desktop — all 3 cards side by side */}
      <div className="hidden lg:grid grid-cols-3 gap-5 2xl:gap-8">
        {cards.map(card => (
          <HeroCard key={card.id || card.title_line1} card={card} navigate={navigate} user={user} />
        ))}
      </div>
    </div>
  );
}

// Softens the top edge of a hero card image into the card's background colour.
// Fully transparent at the very top, fully opaque by HERO_FADE_PX.
const HERO_FADE_PX = 32;
const HERO_IMAGE_FADE =
  `linear-gradient(to bottom, transparent 0px, rgba(0,0,0,0.55) ${HERO_FADE_PX / 2}px, #000 ${HERO_FADE_PX}px)`;

// The mask fades the image into whatever the wrapper is painted with, so the
// wrapper must end on the image's OWN backdrop colour — not the card colour.
// Measured deltas between the declared card bg and the image's top row:
//   shopper  #E1F1E5 vs #DBEFE1 → 12  (imperceptible)
//   nri      #FBE7DA vs #F3D8C1 → 48  (hidden by the fade)
//   business #ECE6F5 vs #D6CCE6 → 63  (still visibly banded)
// So the wrapper ramps card.bg → image_bg across the same distance the image
// fades in. Where the image becomes opaque, the two colours are identical and
// there is nothing left to see. Cards without `image_bg` keep the old
// behaviour, since their gap is already below the visible threshold.
const heroImageBackground = (cardBg, imageBg) =>
  imageBg && imageBg !== cardBg
    ? `linear-gradient(to bottom, ${cardBg} 0px, ${imageBg} ${HERO_FADE_PX}px)`
    : undefined;

// Each hero card's own service page. Older saved content pointed the cards at
// sections of this page (#how-it-works, #services); those now open the service
// page instead. Any other link an admin sets is used as is.
const CARD_SERVICE_PAGE = { nri: '/package-forwarding', shopper: '/personal-shopper', business: '/fulfillment' };
const LEGACY_CARD_ANCHORS = new Set(['', '#', '#how-it-works', '#services']);

export function heroCardHref(card) {
  const href = (card.cta_href || '').trim();
  if (LEGACY_CARD_ANCHORS.has(href) && CARD_SERVICE_PAGE[card.id]) return CARD_SERVICE_PAGE[card.id];
  return href || '#';
}

function HeroCard({ card, navigate, user }) {
  const Icon = CARD_ICONS[card.icon] || ShoppingBag;
  const href = heroCardHref(card);
  const handleCta = (e) => {
    if (href.startsWith('#') || /^https?:/i.test(href)) return; // in-page anchor or external link
    e.preventDefault();
    navigate(href); // a page on this site, e.g. /fulfillment
  };
  return (
    <a
      href={href}
      onClick={handleCta}
      data-testid={`hero-card-${card.id || ''}`}
      className="group block rounded-3xl overflow-hidden border border-[#1A1B1E]/5 transition-all hover:-translate-y-1 hover:shadow-xl"
      style={{ background: card.bg || '#FBE7DA' }}
    >
      <div className="p-7 sm:p-8 flex flex-col h-full">
        <div className="flex items-start justify-between mb-4">
          <span
            className="text-[10px] font-extrabold tracking-[0.16em]"
            style={{ color: card.label_color || '#D96245' }}
          >
            {card.label}
          </span>
          <div
            className="w-9 h-9 rounded-full flex items-center justify-center bg-white/70 backdrop-blur-sm"
            aria-hidden="true"
          >
            <Icon className="w-4.5 h-4.5" style={{ color: card.label_color || '#D96245', width: 18, height: 18 }} />
          </div>
        </div>

        <h3
          className="text-[22px] sm:text-2xl font-black tracking-tight text-[#1A1B1E] leading-[1.15] mb-3"
          style={{ fontFamily: 'Outfit, sans-serif' }}
        >
          {card.title_line1}<br />{card.title_line2}
        </h3>

        <p className="text-sm text-[#4A4D54] leading-relaxed mb-6 max-w-[28ch]">{card.desc}</p>

        {/* CTA pill */}
        <span
          className="inline-flex items-center gap-1.5 self-start text-xs font-bold px-4 py-2 rounded-full bg-white border transition-colors group-hover:bg-[#1A1B1E] group-hover:text-white group-hover:border-[#1A1B1E]"
          style={{ color: card.label_color || '#D96245', borderColor: `${card.label_color || '#D96245'}33` }}
        >
          {card.cta || 'Learn More'} <ArrowRight className="w-3.5 h-3.5" />
        </span>

        {/* Image — natural aspect, fills card width, never cropped on any
            screen size. The backgroundColor on this wrapper is also the
            dominant-colour placeholder shown during the brief image-decode
            gap (Option C).

            Seam handling: card.bg is a declared brand colour while each image
            carries its own baked-in backdrop. They are close but not identical,
            so a butt-join shows a hard horizontal line. We fade the top edge of
            the IMAGE to transparent with a mask, letting the card colour show
            through and blend into the image's own backdrop.

            An earlier attempt painted a card.bg→transparent gradient on top of
            the image instead; that smeared the card colour over image pixels
            whenever the two colours differed, and needed an absolutely
            positioned overlay. Masking touches no pixels below the fade and
            adds no elements, so layout is unchanged. The fade covers only the
            flat backdrop area above each subject. */}
        <div
          className="mt-7 -mx-7 -mb-7 sm:-mx-8 sm:-mb-8 overflow-hidden"
          style={{
            // Ends on the image's own backdrop so the mask blends like-to-like.
            backgroundColor: card.image_bg || card.bg || '#FBE7DA',
            backgroundImage: heroImageBackground(card.bg || '#FBE7DA', card.image_bg),
            minHeight: card.image ? undefined : 160,
          }}
        >
          {resolveImageUrl(card.image) ? (
            <ResilientImg
              src={card.image}
              alt=""
              loading="eager"
              decoding="async"
              fetchPriority="high"
              sizes="(min-width: 1024px) 33vw, 100vw"
              className="block w-full h-auto select-none"
              draggable={false}
              style={{
                imageRendering: 'auto',
                backgroundColor: card.image_bg || card.bg || '#FBE7DA',
                WebkitMaskImage: HERO_IMAGE_FADE,
                maskImage: HERO_IMAGE_FADE,
              }}
            />
          ) : null}
        </div>
      </div>
    </a>
  );
}

export default function LandingPage() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [countries, setCountries] = useState([]);
  const [selectedCountry, setSelectedCountry] = useState('US');
  const [weight, setWeight] = useState('1');
  const [rates, setRates] = useState(null);
  const [calcLoading, setCalcLoading] = useState(false);
  const [openFaq, setOpenFaq] = useState(null);
  const [c, setC] = useState(DEFAULTS);
  const [showVolumetric, setShowVolumetric] = useState(false);
  const [dims, setDims] = useState({ length: '', width: '', height: '' });
  const [logo, setLogo] = useState(() => {
    try {
      const cached = localStorage.getItem('shipdesi_logo');
      return cached ? withResolvedLogo(JSON.parse(cached)) : defaultLogoState();
    } catch {
      return defaultLogoState();
    }
  });
  const [apiPlans, setApiPlans] = useState([]);
  const [blogPosts, setBlogPosts] = useState([]);
  const [legalPages, setLegalPages] = useState([]);

  useEffect(() => {
    getCountries().then(r => setCountries(r.data)).catch(() => {});
    getPublicContent().then(r => {
      if (r.data && r.data.hero) setC(prev => mergeDeep(prev, r.data));
    }).catch(() => {});
    // Refresh logo in background; first paint already uses /branding/logo.png
    getLogoConfig().then(r => {
      if (r.data) {
        const resolved = withResolvedLogo(r.data);
        setLogo(resolved);
        try { localStorage.setItem('shipdesi_logo', JSON.stringify(resolved)); } catch {}
      }
    }).catch(() => {});
  }, []);

  // Defer below-the-fold marketing fetches until idle
  useEffect(() => {
    let cancelled = false;
    const run = () => {
      if (cancelled) return;
      getPlans().then(r => { if (!cancelled && r.data && r.data.length) setApiPlans(r.data); }).catch(() => {});
      getPublicBlogPosts(3).then(r => { if (!cancelled) setBlogPosts(r.data.posts || []); }).catch(() => {});
      getPublicStaticPages().then(r => { if (!cancelled) setLegalPages(r.data.pages || []); }).catch(() => {});
    };
    if (typeof window !== 'undefined' && 'requestIdleCallback' in window) {
      const id = window.requestIdleCallback(run, { timeout: 2500 });
      return () => { cancelled = true; window.cancelIdleCallback?.(id); };
    }
    const t = setTimeout(run, 400);
    return () => { cancelled = true; clearTimeout(t); };
  }, []);

  // Prefetch common marketing route chunks on idle (faster in-app navigation)
  useEffect(() => {
    const prefetch = () => {
      import('./HowItWorks');
      import('./Pricing');
      import('./FAQ');
      import('./Contact');
      import('./ShippingCalculator');
    };
    if (typeof window !== 'undefined' && 'requestIdleCallback' in window) {
      const id = window.requestIdleCallback(prefetch, { timeout: 4000 });
      return () => window.cancelIdleCallback?.(id);
    }
    const t = setTimeout(prefetch, 1200);
    return () => clearTimeout(t);
  }, []);

  // Only preload hero images that actually exist (skip empty / broken URLs)
  useEffect(() => {
    const cards = (c.hero && c.hero.cards) || [];
    const links = [];
    cards.forEach(card => {
      if (!card.image) return;
      const link = document.createElement('link');
      link.rel = 'preload';
      link.as = 'image';
      link.href = card.image;
      link.fetchpriority = 'high';
      document.head.appendChild(link);
      links.push(link);
    });
    return () => { links.forEach(l => { try { document.head.removeChild(l); } catch {} }); };
  }, [c.hero]);

  const volWeight = showVolumetric && dims.length && dims.width && dims.height
    ? (parseFloat(dims.length) * parseFloat(dims.width) * parseFloat(dims.height)) / 5000
    : 0;
  const billableWeight = Math.max(parseFloat(weight) || 0, volWeight);

  const handleCalculate = async () => {
    if (!billableWeight || billableWeight <= 0) return;
    setCalcLoading(true);
    try {
      const res = await getQuote(selectedCountry, billableWeight);
      setRates(res.data.rates);
    } catch { }
    setCalcLoading(false);
  };

  const services = c.services.items;
  const svcColors = ['bg-[#FFE5D9]', 'bg-[#D8E2DC]', 'bg-[#BDE0FE]', 'bg-[#FCD5CE]'];
  const svcIcons = [<Package className="w-7 h-7" />, <Globe className="w-7 h-7" />, <Shield className="w-7 h-7" />, <CreditCard className="w-7 h-7" />];
  const pricing = c.pricing.plans;
  const faqs = c.faq.items;

  return (
    <div className="min-h-screen bg-[#FAFAF8]">
      <Helmet>
        <title>ShipDesi - Ship from India to the World | Virtual Indian Address</title>
        <meta name="description" content="Ship from India to the USA, Canada, and the UK with a free virtual Mumbai address. Shop Indian stores, consolidate packages, and forward them worldwide with ShipDesi." />
        <meta property="og:title" content="ShipDesi - Ship from India to the World" />
        <meta property="og:description" content="Ship from India to the USA, Canada, and the UK with a free virtual Mumbai address." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://shipdesi.com/" />
        <meta name="twitter:card" content="summary_large_image" />
        <link rel="canonical" href="https://shipdesi.com/" />
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Organization",
          "name": "ShipDesi",
          "url": "https://shipdesi.com",
          "description": "Ship from India to the USA, Canada, and the UK with a free virtual Mumbai address. Shop Indian stores, consolidate packages, and forward them worldwide with ShipDesi.",
          "sameAs": [],
          "contactPoint": { "@type": "ContactPoint", "contactType": "customer service", "availableLanguage": "English" }
        })}</script>
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Service",
          "name": "International Parcel Forwarding",
          "provider": { "@type": "Organization", "name": "ShipDesi" },
          "description": "Ship from India to the USA, Canada, and the UK with a free virtual Mumbai address. Shop Indian stores, consolidate packages, and forward them worldwide with ShipDesi.",
          "areaServed": "Worldwide",
          "serviceType": "Parcel Forwarding"
        })}</script>
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          "mainEntity": (c?.faq?.items || []).map(f => ({
            "@type": "Question",
            "name": f.q,
            "acceptedAnswer": { "@type": "Answer", "text": f.a }
          }))
        })}</script>
      </Helmet>
      <MarketingHeader logo={logo} />

      {/* Hero */}
      <section className="pt-32 sm:pt-36 lg:pt-40 pb-12 lg:pb-16 px-6 relative overflow-hidden" data-testid="hero-section">
        <div className="max-w-7xl 2xl:max-w-[1500px] mx-auto relative z-10">
          {/* Trust badge */}
          <div className="flex justify-center mb-6 animate-fade-in-up">
            <div className="inline-flex items-center gap-2 bg-[#FFE5D9] text-[#1A1B1E] px-4 py-1.5 rounded-full text-[11px] font-bold tracking-[0.12em] border border-[#1A1B1E]/10" data-testid="hero-trust-badge">
              <Star className="w-3.5 h-3.5" /> {c.hero.badge}
            </div>
          </div>

          {/* Headline */}
          <h1
            className="text-center text-4xl sm:text-5xl lg:text-6xl 2xl:text-7xl font-black tracking-tight text-[#1A1B1E] leading-[1.05] mb-5 animate-fade-in-up"
            style={{ fontFamily: 'Outfit, sans-serif' }}
            data-testid="hero-headline"
          >
            {c.hero.title_line1}
            <br />
            {c.hero.title_line2_prefix}
            <span className="text-[#D96245]">{c.hero.title_line2_accent}</span>
          </h1>

          {/* Subtitle */}
          <p className="text-center text-base sm:text-lg 2xl:text-xl text-[#4A4D54] max-w-2xl 2xl:max-w-3xl mx-auto mb-12 animate-fade-in-up delay-100" data-testid="hero-subtitle">
            {c.hero.subtitle}
          </p>

          {/* Service cards (3-up on lg, 1-at-a-time carousel below lg) */}
          <HeroCards cards={c.hero.cards} autoplaySeconds={c.hero.autoplay_seconds} navigate={navigate} user={user} />
        </div>
      </section>

      {/* Calculator */}
      <section id="calculator" className="py-20 px-6 bg-[#FFE5D9]/30 relative z-20" data-testid="calculator-section">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-10 animate-fade-in-up">
            <div className="inline-flex items-center gap-2 text-xs font-bold tracking-[0.2em] uppercase text-[#4A4D54] mb-3">
              <Calculator className="w-4 h-4" /> {c.calculator.label}
            </div>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-[#1A1B1E]" style={{ fontFamily: 'Outfit, sans-serif' }}>
              {c.calculator.title}
            </h2>
          </div>
          <div className="card-brutal rounded-2xl p-8 bg-white animate-fade-in-up delay-100">
            <div className="grid sm:grid-cols-3 gap-6 mb-4">
              <div>
                <label className="text-xs font-bold tracking-wide uppercase text-[#4A4D54] mb-2 block">Destination</label>
                <Select value={selectedCountry} onValueChange={setSelectedCountry}>
                  <SelectTrigger className="w-full h-12 border-[#1A1B1E] rounded-xl focus:ring-[#FFE5D9]" data-testid="country-select">
                    <SelectValue placeholder="Select country" />
                  </SelectTrigger>
                  <SelectContent>
                    {countries.map(c => (
                      <SelectItem key={c.code} value={c.code}>{c.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs font-bold tracking-wide uppercase text-[#4A4D54] mb-2 block">Actual Weight (kg)</label>
                <input type="number" value={weight} onChange={e => setWeight(e.target.value)} min="0.1" step="0.1" data-testid="weight-input"
                  className="w-full h-12 px-4 border border-[#1A1B1E] rounded-xl focus:ring-2 focus:ring-[#FFE5D9] outline-none font-medium" />
              </div>
              <div className="flex items-end">
                <button onClick={handleCalculate} disabled={calcLoading} data-testid="calculate-shipping-btn"
                  className="w-full h-12 bg-[#1A1B1E] text-white rounded-xl font-bold text-sm hover:-translate-y-0.5 hover:shadow-lg transition-all disabled:opacity-50">
                  {calcLoading ? 'Calculating...' : 'Get Rates'}
                </button>
              </div>
            </div>

            {/* Volumetric Weight Toggle */}
            <div className="mb-4">
              <label className="flex items-center gap-2.5 cursor-pointer select-none group" data-testid="volumetric-toggle">
                <input type="checkbox" checked={showVolumetric} onChange={e => setShowVolumetric(e.target.checked)}
                  className="w-4 h-4 rounded border-[#1A1B1E] text-[#D96245] focus:ring-[#D96245] cursor-pointer" />
                <span className="text-sm font-semibold text-[#4A4D54] group-hover:text-[#1A1B1E] transition-colors flex items-center gap-1.5">
                  <Ruler className="w-3.5 h-3.5" /> Volumetric Weight (Optional)
                </span>
              </label>
              {showVolumetric && (
                <div className="mt-3 p-4 rounded-xl bg-[#F6F5F2] border border-[#EBE8E4] animate-fade-in-up">
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    <div>
                      <label className="text-[10px] font-bold tracking-wide uppercase text-[#8F8984] mb-1 block">Length (cm)</label>
                      <input type="number" value={dims.length} onChange={e => setDims(d => ({ ...d, length: e.target.value }))} min="1" step="1"
                        data-testid="vol-length" placeholder="cm"
                        className="w-full h-10 px-3 border border-[#EBE8E4] rounded-lg focus:ring-2 focus:ring-[#D96245] outline-none text-sm font-medium" />
                    </div>
                    <div>
                      <label className="text-[10px] font-bold tracking-wide uppercase text-[#8F8984] mb-1 block">Width (cm)</label>
                      <input type="number" value={dims.width} onChange={e => setDims(d => ({ ...d, width: e.target.value }))} min="1" step="1"
                        data-testid="vol-width" placeholder="cm"
                        className="w-full h-10 px-3 border border-[#EBE8E4] rounded-lg focus:ring-2 focus:ring-[#D96245] outline-none text-sm font-medium" />
                    </div>
                    <div>
                      <label className="text-[10px] font-bold tracking-wide uppercase text-[#8F8984] mb-1 block">Height (cm)</label>
                      <input type="number" value={dims.height} onChange={e => setDims(d => ({ ...d, height: e.target.value }))} min="1" step="1"
                        data-testid="vol-height" placeholder="cm"
                        className="w-full h-10 px-3 border border-[#EBE8E4] rounded-lg focus:ring-2 focus:ring-[#D96245] outline-none text-sm font-medium" />
                    </div>
                  </div>
                  {volWeight > 0 && (
                    <div className="mt-3 flex items-center justify-between text-xs">
                      <span className="text-[#8F8984]">Volumetric: <strong className="text-[#2C2825]">{volWeight.toFixed(1)} kg</strong> (L×W×H / 5000)</span>
                      <span className="text-[#D96245] font-bold">Billable: {billableWeight.toFixed(1)} kg</span>
                    </div>
                  )}
                </div>
              )}
            </div>

            {rates && (
              <div className="space-y-3 pt-4 border-t border-gray-200" data-testid="rate-results">
                {rates.map((r, i) => (
                  <div key={i} className={`flex items-center justify-between p-4 rounded-xl ${i === 0 ? 'bg-[#D8F3DC]' : 'bg-gray-50'}`}>
                    <div className="flex items-center gap-3">
                      {resolveImageUrl(r.logo_url) ? (
                        <ResilientImg
                          src={r.logo_url}
                          alt=""
                          className="w-8 h-8 rounded-lg object-contain border border-gray-200 bg-white"
                          onFailed={() => (
                            <div className="w-8 h-8 rounded-lg bg-gray-100 border border-gray-200 flex items-center justify-center">
                              <Package className="w-4 h-4 text-gray-400" />
                            </div>
                          )}
                        />
                      ) : (
                        <div className="w-8 h-8 rounded-lg bg-gray-100 border border-gray-200 flex items-center justify-center">
                          <Package className="w-4 h-4 text-gray-400" />
                        </div>
                      )}
                      <div>
                        <p className="font-bold text-sm">{r.courier}</p>
                        <p className="text-xs text-[#4A4D54]">{r.estimated_days}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="font-black text-lg">{'\u20B9'}{Math.round(r.price_usd).toLocaleString('en-IN')}</p>
                      {i === 0 && <span className="text-xs font-bold text-green-700">Best Rate</span>}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </section>

      {/* How It Works - 5 Steps */}
      <section id="how-it-works" className="py-24 px-6 relative overflow-hidden" data-testid="how-it-works-section">
        <div className="absolute inset-0 bg-gradient-to-b from-white via-[#FFF8F5] to-white pointer-events-none" />
        <div className="max-w-6xl mx-auto relative z-10">
          <div className="text-center mb-16 animate-fade-in-up">
            <p className="text-xs font-bold tracking-[0.2em] uppercase text-[#D96245] mb-3">HOW IT WORKS</p>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-[#1A1B1E]" style={{ fontFamily: 'Outfit, sans-serif' }}>
              How <span className="text-[#D96245]">ShipDesi</span> Works
            </h2>
            <p className="text-base text-[#4A4D54] mt-3 max-w-xl mx-auto">From your favourite Indian store to your doorstep — anywhere in the world</p>
          </div>

          {/* Desktop: Horizontal flow */}
          <div className="hidden lg:block">
            <div className="grid grid-cols-5 gap-0 items-start">
              {[
                { icon: <UserPlus className="w-7 h-7" />, title: 'Sign Up', desc: 'Register for free and get your unique Indian virtual warehouse address instantly.', color: 'bg-[#FFE5D9]', accent: '#D96245' },
                { icon: <ShoppingCart className="w-7 h-7" />, title: 'Shop', desc: 'Buy from Amazon, Flipkart, Myntra or any Indian store. Use your ShipDesi address as delivery.', color: 'bg-[#D8E2DC]', accent: '#3A805A' },
                { icon: <Warehouse className="w-7 h-7" />, title: 'We Receive', desc: 'Your packages arrive at our warehouse. We photograph, weigh, and safely store them.', color: 'bg-[#BDE0FE]', accent: '#2563EB' },
                { icon: <Combine className="w-7 h-7" />, title: 'Save', desc: 'Consolidate parcels from different stores into one shipment. Save up to 80% on shipping!', color: 'bg-[#FEF3C7]', accent: '#D97706' },
                { icon: <Truck className="w-7 h-7" />, title: 'Ship & Receive', desc: 'Choose your courier, pay securely, and receive your package at your door within 5-7 days.', color: 'bg-[#FCD5CE]', accent: '#DC2626' },
              ].map((step, i) => (
                <div key={i} className="flex flex-col items-center text-center relative">
                  {/* Connector arrow */}
                  {i < 4 && (
                    <div className="absolute top-10 left-[60%] w-[80%] flex items-center justify-center z-0">
                      <div className="w-full border-t-2 border-dashed border-[#D96245]/25" />
                      <ChevronRight className="w-4 h-4 text-[#D96245]/40 absolute -right-2 flex-shrink-0" />
                    </div>
                  )}
                  {/* Step number + icon */}
                  <div className="relative z-10 mb-5">
                    <div className={`w-20 h-20 rounded-2xl ${step.color} flex items-center justify-center shadow-sm`}
                      style={{ color: step.accent }}>
                      {step.icon}
                    </div>
                    <div className="absolute -top-2 -right-2 w-7 h-7 rounded-full bg-[#1A1B1E] text-white flex items-center justify-center">
                      <span className="text-[11px] font-black">{i + 1}</span>
                    </div>
                  </div>
                  <h3 className="text-base font-bold tracking-tight text-[#1A1B1E] mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>{step.title}</h3>
                  <p className="text-xs text-[#4A4D54] leading-relaxed px-2 max-w-[180px]">{step.desc}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Mobile/Tablet: Vertical flow */}
          <div className="lg:hidden space-y-0">
            {[
              { icon: <UserPlus className="w-6 h-6" />, title: 'Sign Up', desc: 'Register for free and get your unique Indian virtual warehouse address instantly.', color: 'bg-[#FFE5D9]', accent: '#D96245' },
              { icon: <ShoppingCart className="w-6 h-6" />, title: 'Shop', desc: 'Buy from Amazon, Flipkart, Myntra or any Indian store. Use your ShipDesi address as delivery.', color: 'bg-[#D8E2DC]', accent: '#3A805A' },
              { icon: <Warehouse className="w-6 h-6" />, title: 'We Receive', desc: 'Your packages arrive at our warehouse. We photograph, weigh, and safely store them.', color: 'bg-[#BDE0FE]', accent: '#2563EB' },
              { icon: <Combine className="w-6 h-6" />, title: 'Save', desc: 'Consolidate parcels from different stores into one shipment. Save up to 80% on shipping!', color: 'bg-[#FEF3C7]', accent: '#D97706' },
              { icon: <Truck className="w-6 h-6" />, title: 'Ship & Receive', desc: 'Choose your courier, pay securely, and receive your package at your door within 5-7 days.', color: 'bg-[#FCD5CE]', accent: '#DC2626' },
            ].map((step, i) => (
              <div key={i} className="flex gap-5 items-start">
                {/* Left: icon + connector */}
                <div className="flex flex-col items-center flex-shrink-0">
                  <div className={`w-14 h-14 rounded-xl ${step.color} flex items-center justify-center relative`} style={{ color: step.accent }}>
                    {step.icon}
                    <div className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-[#1A1B1E] text-white flex items-center justify-center">
                      <span className="text-[9px] font-black">{i + 1}</span>
                    </div>
                  </div>
                  {i < 4 && <div className="w-0.5 h-8 bg-[#D96245]/20 mt-2" />}
                </div>
                {/* Right: text */}
                <div className="pb-6">
                  <h3 className="text-base font-bold tracking-tight text-[#1A1B1E] mb-1" style={{ fontFamily: 'Outfit, sans-serif' }}>{step.title}</h3>
                  <p className="text-sm text-[#4A4D54] leading-relaxed">{step.desc}</p>
                </div>
              </div>
            ))}
          </div>

          {/* CTA */}
          <div className="text-center mt-14 animate-fade-in-up">
            <button onClick={() => user ? navigate('/dashboard') : navigate('/login')}
              className="bg-[#D96245] text-white px-10 py-4 rounded-full text-sm font-bold hover:-translate-y-1 hover:shadow-xl transition-all inline-flex items-center gap-2">
              Get Started Free <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </section>

      {/* Services */}
      <section id="services" className="py-20 px-6 bg-[#D8E2DC]/20" data-testid="services-section">
        <div className="max-w-7xl 2xl:max-w-[1500px] mx-auto">
          <div className="text-center mb-16 animate-fade-in-up">
            <p className="text-xs font-bold tracking-[0.2em] uppercase text-[#4A4D54] mb-3">{c.services.label}</p>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-[#1A1B1E]" style={{ fontFamily: 'Outfit, sans-serif' }}>
              {c.services.title}
            </h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {services.map((s, i) => (
              <div key={i} className={`card-brutal rounded-2xl p-6 animate-fade-in-up delay-${(i + 1) * 100}`}>
                <div className={`w-14 h-14 rounded-xl ${svcColors[i]} border border-[#1A1B1E] flex items-center justify-center mb-4`}>
                  {svcIcons[i]}
                </div>
                <h3 className="text-lg font-bold tracking-tight mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>{s.title}</h3>
                <p className="text-sm text-[#4A4D54] leading-relaxed">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" className="py-20 px-6" data-testid="pricing-section">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-16 animate-fade-in-up">
            <p className="text-xs font-bold tracking-[0.2em] uppercase text-[#4A4D54] mb-3">PRICING</p>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-[#1A1B1E]" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Simple, Transparent Pricing
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {(apiPlans.length > 0 ? apiPlans : c.pricing.plans.map((p, i) => ({
              plan_id: p.name.toLowerCase(), name: p.name,
              price: Number(String(p.price).replace(/[^\d]/g, '')) || 0,
              billing_cycle: /yr/.test(p.price) ? 'yearly' : /mo/.test(p.price) ? 'monthly' : 'free',
              features: p.features, cta: p.cta, popular: p.popular,
              consolidation_fee_per_pkg: 50, shipping_discount_pct: 0, shopper_fee_pct: 7, shopper_min_fee: 200, photo_fee: 5, free_storage_days: 20,
            }))).map((plan, i) => (
              <div key={plan.plan_id || i} className={`card-brutal rounded-2xl p-8 animate-fade-in-up delay-${(i + 1) * 100} ${plan.popular ? 'bg-[#1A1B1E] text-white relative' : 'bg-white'}`}>
                {plan.popular && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#FFE5D9] text-[#1A1B1E] px-4 py-1 rounded-full text-xs font-bold border border-[#1A1B1E]">MOST POPULAR</div>
                )}
                <h3 className="text-lg font-bold mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>{plan.name}</h3>
                <div className="mb-4">
                  <span className="text-3xl font-black" style={{ fontFamily: 'Outfit, sans-serif' }}>
                    {plan.price === 0 ? 'Free' : `\u20B9${plan.price.toLocaleString('en-IN')}`}
                  </span>
                  {plan.billing_cycle && plan.billing_cycle !== 'free' && (
                    <span className={`text-sm ml-1 ${plan.popular ? 'text-gray-300' : 'text-[#8F8984]'}`}>/{plan.billing_cycle === 'monthly' ? 'mo' : 'yr'}</span>
                  )}
                </div>
                {/* Key details */}
                <div className="space-y-1.5 mb-5">
                  {plan.consolidation_fee_per_pkg !== undefined && (
                    <div className={`flex justify-between text-xs ${plan.popular ? 'text-gray-300' : 'text-[#8F8984]'}`}>
                      <span>Consolidation</span>
                      <span className={`font-bold ${plan.popular ? 'text-white' : 'text-[#2C2825]'}`}>{plan.consolidation_fee_per_pkg === 0 ? 'Free' : `\u20B9${plan.consolidation_fee_per_pkg}/pkg`}</span>
                    </div>
                  )}
                  {plan.shipping_discount_pct !== undefined && (
                    <div className={`flex justify-between text-xs ${plan.popular ? 'text-gray-300' : 'text-[#8F8984]'}`}>
                      <span>Shipping Discount</span>
                      <span className={`font-bold ${plan.popular ? 'text-white' : 'text-[#2C2825]'}`}>{plan.shipping_discount_pct === 0 ? '-' : `${plan.shipping_discount_pct}%`}</span>
                    </div>
                  )}
                  {plan.free_storage_days !== undefined && (
                    <div className={`flex justify-between text-xs ${plan.popular ? 'text-gray-300' : 'text-[#8F8984]'}`}>
                      <span>Free Storage</span>
                      <span className={`font-bold ${plan.popular ? 'text-white' : 'text-[#2C2825]'}`}>{plan.free_storage_days} days</span>
                    </div>
                  )}
                </div>
                <ul className="space-y-3 mb-8">
                  {(plan.features || []).map((f, j) => (
                    <li key={j} className="flex items-center gap-2 text-sm">
                      <ChevronRight className="w-4 h-4 flex-shrink-0" /> {f}
                    </li>
                  ))}
                </ul>
                <button onClick={() => navigate('/login')} data-testid={`pricing-cta-${(plan.plan_id || plan.name || '').toLowerCase()}`}
                  className={`w-full py-3 rounded-xl font-bold text-sm transition-all hover:-translate-y-0.5 ${
                    plan.popular ? 'bg-white text-[#1A1B1E] hover:shadow-lg' : 'bg-[#1A1B1E] text-white hover:shadow-lg'
                  }`}>
                  {plan.cta || 'Get Started'}
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Blog Preview Section */}
      {blogPosts.length > 0 && (
        <section className="py-20 px-6" data-testid="blog-preview-section">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <p className="text-xs font-bold tracking-[0.2em] uppercase text-[#D96245] mb-3">FROM OUR BLOG</p>
              <h2 className="text-2xl sm:text-3xl font-bold tracking-tight text-[#1A1B1E]" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Shipping Guides & Tips
              </h2>
            </div>
            <div className="grid md:grid-cols-3 gap-6">
              {blogPosts.map(post => (
                <a key={post.post_id} href={`/blog/${post.slug}`}
                  className="group bg-white rounded-2xl border border-[#EBE8E4] overflow-hidden hover:border-[#D96245]/40 hover:shadow-lg transition-all duration-300"
                  data-testid={`blog-preview-${post.slug}`}>
                  {resolveImageUrl(post.cover_image) && (
                    <ResilientImg
                      src={post.cover_image}
                      alt={post.title}
                      className="w-full h-40 object-cover"
                      loading="lazy"
                    />
                  )}
                  <div className="p-5">
                    <div className="flex flex-wrap gap-1.5 mb-2">
                      {post.tags?.slice(0, 2).map((tag, i) => (
                        <span key={i} className="px-2 py-0.5 rounded-full text-[9px] font-bold bg-[#D96245]/10 text-[#D96245]">{tag}</span>
                      ))}
                    </div>
                    <h3 className="font-bold text-sm text-[#2C2825] group-hover:text-[#D96245] transition-colors line-clamp-2 mb-1.5">{post.title}</h3>
                    {post.excerpt && <p className="text-xs text-[#8F8984] line-clamp-2">{post.excerpt}</p>}
                  </div>
                </a>
              ))}
            </div>
            <div className="text-center mt-8">
              <a href="/blog" className="text-sm font-bold text-[#D96245] hover:underline">View All Posts &rarr;</a>
            </div>
          </div>
        </section>
      )}

      {/* FAQ */}
      <section id="faq" className="py-20 px-6 bg-[#E8E8E4]/30" data-testid="faq-section">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-16 animate-fade-in-up">
            <p className="text-xs font-bold tracking-[0.2em] uppercase text-[#4A4D54] mb-3">{c.faq.label}</p>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-[#1A1B1E]" style={{ fontFamily: 'Outfit, sans-serif' }}>
              {c.faq.title}
            </h2>
          </div>
          <div className="space-y-3">
            {faqs.map((faq, i) => (
              <div key={i} className="card-brutal rounded-xl bg-white animate-fade-in-up" data-testid={`faq-item-${i}`}>
                <button onClick={() => setOpenFaq(openFaq === i ? null : i)} className="w-full p-5 flex items-center justify-between text-left">
                  <span className="font-bold text-sm pr-4">{faq.q}</span>
                  <ChevronDown className={`w-5 h-5 flex-shrink-0 transition-transform ${openFaq === i ? 'rotate-180' : ''}`} />
                </button>
                {openFaq === i && (
                  <div className="px-5 pb-5 text-sm text-[#4A4D54] leading-relaxed border-t border-gray-100 pt-3">{faq.a}</div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#1A1B1E] text-white py-16 px-6" data-testid="footer">
        <div className="max-w-7xl 2xl:max-w-[1500px] mx-auto">
          <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-12 mb-12">
            <div>
              <a href="/" className="flex items-center gap-2 mb-4" data-testid="footer-logo-link">
                {logo.logo_url ? (
                  <img src={logo.logo_url} alt="ShipDesi" style={{ height: `${logo.footer_height}px` }} className="w-auto brightness-0 invert" />
                ) : (
                  <span className="text-lg font-black tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>ShipDesi</span>
                )}
              </a>
              <p className="text-sm text-gray-400 leading-relaxed">{c.footer.tagline}</p>
              <p className="text-xs text-gray-500 mt-4 leading-relaxed" data-testid="footer-brand-owner">
                ShipDesi is a brand owned and operated by <span className="text-gray-300 font-semibold">AG Renai &amp; Co.</span>
              </p>
            </div>
            <div>
              <h4 className="font-bold text-sm mb-4">Quick Links</h4>
              <div className="space-y-2">
                <Link to="/how-it-works" className="block text-sm text-gray-400 hover:text-white transition-colors">How It Works</Link>
                <Link to="/package-forwarding" className="block text-sm text-gray-400 hover:text-white transition-colors">Package Forwarding</Link>
                <Link to="/personal-shopper" className="block text-sm text-gray-400 hover:text-white transition-colors">Personal Shopper</Link>
                <Link to="/fulfillment" className="block text-sm text-gray-400 hover:text-white transition-colors">Fulfillment</Link>
                <Link to="/pricing" className="block text-sm text-gray-400 hover:text-white transition-colors">Pricing</Link>
                <Link to="/shipping-calculator" className="block text-sm text-gray-400 hover:text-white transition-colors">Calculator</Link>
                <Link to="/faq" className="block text-sm text-gray-400 hover:text-white transition-colors">FAQ</Link>
                <Link to="/blog" className="block text-sm text-gray-400 hover:text-white transition-colors">Blog</Link>
                <Link to="/contact" className="block text-sm text-gray-400 hover:text-white transition-colors" data-testid="footer-quick-contact">Contact</Link>
              </div>
            </div>
            <div data-testid="footer-legal-column">
              <h4 className="font-bold text-sm mb-4">Legal</h4>
              <div className="space-y-2">
                {legalPages.map((p) => (
                  <a
                    key={p.slug}
                    href={`/legal/${p.slug}`}
                    className="block text-sm text-gray-400 hover:text-white transition-colors"
                    data-testid={`footer-legal-${p.slug}`}
                  >
                    {p.title}
                  </a>
                ))}
              </div>
            </div>
            <div data-testid="footer-ship-to-column">
              <h4 className="font-bold text-sm mb-4">Ship To</h4>
              <div className="space-y-2">
                <Link to="/shop-from-india-usa" className="block text-sm text-gray-400 hover:text-white transition-colors" data-testid="home-footer-country-usa"><span className="mr-1.5">🇺🇸</span>Ship to USA</Link>
                <Link to="/shop-from-india-canada" className="block text-sm text-gray-400 hover:text-white transition-colors" data-testid="home-footer-country-canada"><span className="mr-1.5">🇨🇦</span>Ship to Canada</Link>
                <Link to="/shop-from-india-uk" className="block text-sm text-gray-400 hover:text-white transition-colors" data-testid="home-footer-country-uk"><span className="mr-1.5">🇬🇧</span>Ship to UK</Link>
                <Link to="/shop-from-india-australia" className="block text-sm text-gray-400 hover:text-white transition-colors" data-testid="home-footer-country-australia"><span className="mr-1.5">🇦🇺</span>Ship to Australia</Link>
                <Link to="/shop-from-india-uae" className="block text-sm text-gray-400 hover:text-white transition-colors" data-testid="home-footer-country-uae"><span className="mr-1.5">🇦🇪</span>Ship to UAE</Link>
              </div>
            </div>
            <div>
              <h4 className="font-bold text-sm mb-4">Contact Us</h4>
              <div className="space-y-3">
                <div className="flex items-center gap-2 text-sm text-gray-400"><Mail className="w-4 h-4" /> {c.footer.email}</div>
                <div className="text-sm text-gray-400 leading-relaxed" data-testid="footer-address">
                  <div className="flex items-start gap-2">
                    <MapPin className="w-4 h-4 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="font-semibold text-gray-300">AG Renai &amp; Co</p>
                      <p>15/773 Karanjikudy</p>
                      <p>Perumbavoor, Kerala</p>
                      <p>India</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="border-t border-gray-800 pt-8 text-center text-xs text-gray-500">
            &copy; {new Date().getFullYear()} AG Renai &amp; Co. All rights reserved. ShipDesi is a registered brand of AG Renai &amp; Co.
          </div>
        </div>
      </footer>
    </div>
  );
}
